import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { BUSINESS_TOOL_SECTIONS } from 'constants/businessTool';
import { BUSINESS_TOOL } from 'constants/paths';
import * as PATHS from 'constants/paths';
import { merge } from 'lodash';
import { useCreateBusinessPlan } from 'services/businessPlan/hooks';
import { getDefaultBusinessPlan, useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getSetActiveSection,
  getSetActiveSubSection,
  getSetBusinessPlanType,
} from 'store/useBusinessToolStore/selectors';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { useGetGlobalInitialValues } from 'components/BusinessToolForm/Sections/Global/form';

import { insertId } from 'helpers/path';
import { getActiveFormSection, getActiveFormSubSection } from 'helpers/setActiveFormSection';
import ToastService from 'helpers/ToastService';

import { ICreateBusinessPlanHandlerProps } from '../types';

export const useCreateBusinessPlanHandler = ({
  isPlanAvailable,
  type,
  sections = [],
}: ICreateBusinessPlanHandlerProps) => {
  const navigate = useNavigate();

  const createBusinessPlan = useCreateBusinessPlan();

  const { isAuthenticated, isAuthConfiguring } = useAuthenticated();

  const defaultBusinessPlan = getDefaultBusinessPlan();
  const setBusinessPlanType = useBusinessToolStore(getSetBusinessPlanType);
  const setActiveSection = useBusinessToolStore(getSetActiveSection);
  const setActiveSubSection = useBusinessToolStore(getSetActiveSubSection);
  const initialGlobal = useGetGlobalInitialValues();

  const createBusinessPlanHandler = useCallback(async () => {
    setBusinessPlanType(type);
    setActiveSection(getActiveFormSection(type));
    setActiveSubSection(getActiveFormSubSection(type));

    if (!(isAuthenticated || isAuthConfiguring)) {
      return navigate(PATHS.HOME);
    }

    if (!isPlanAvailable || createBusinessPlan.isPending) return;

    try {
      const language = localStorage.getItem('language');
      const newGlobalSectionContent = merge(initialGlobal, {
        documentLanguage: language && language === 'de' ? 'Deutsch' : 'English',
      });
      const newGlobalChapter = {
        chapterName: BUSINESS_TOOL_SECTIONS.GLOBAL,
        content: JSON.stringify(newGlobalSectionContent),
      };
      const newSections = [...sections, newGlobalChapter];
      const newBusinessPlan = await createBusinessPlan.mutateAsync({
        title: 'Document',
        type,
        sections: newSections,
        metadata: defaultBusinessPlan.metadata,
      });
      localStorage.removeItem('freePlanClicked');
      localStorage.removeItem('typeFormDataId');
      navigate(insertId(BUSINESS_TOOL, newBusinessPlan.uuid));
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }
    }
  }, [isPlanAvailable, createBusinessPlan.isPending, isAuthenticated, isAuthConfiguring, sections]);

  return { createBusinessPlanHandler, isPending: createBusinessPlan.isPending };
};

export default useCreateBusinessPlanHandler;
